import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarClock, LuClipboardPen, LuHospital, LuHouse, LuMapPin, LuPhone, LuRepeat, LuStickyNote, LuUser, LuCalendarX } from 'react-icons/lu';
import { Field, TextArea, TextInput } from '../../../../components/form/FormControls';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { resolveCanonicalCenterId } from '../../../../utils/centerDedupe';
import { normalizePatientId } from '../../../../utils/patientAccess';
import { VISIT_TYPES } from '../visitSteps';
import { CheckRow, ChoiceChips, FIELD_GRID, MissingTag, NativeSelect, SectionCard, fieldAnchor } from '../VisitUi';

const VISIT_TYPE_ICONS = {
  hastane: LuHospital,
  home: LuHouse,
  telefon: LuPhone,
  unplanning: LuCalendarX,
  handover: LuRepeat,
};

/**
 * "Ziyaret Formu". Every control writes through the modal's own handlers
 * (handleInputChange / handleHospitalChange) to the same formData keys as
 * before; nothing is saved until "Kaydet" or "Ziyareti Tamamla".
 */
const VisitFormStep = ({
  formData,
  isReadOnly,
  loading,
  loadingPatients,
  projectPatients,
  myProjectCenters,
  otherCenters,
  canonicalIdById,
  cities,
  locationAutoFilledFrom,
  missingKeys,
  onChange,
  onHospitalChange,
}) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`visitModal.form.${key}`, vars);
  const missingIn = (section) => {
    const keys = { type: ['visitType', 'sourceDocument'], time: ['date', 'startTime', 'endTime'], patient: ['patientId', 'doctor'], location: ['hospital', 'city', 'travelType'] }[section];
    return keys.filter((key) => missingKeys.has(key)).length;
  };
  const tag = (key) => (missingKeys.has(key) ? <MissingTag /> : null);
  const id = (key) => fieldAnchor(key);

  // The end date field writes the literal 'visitEvaluation.visitEndDate' key, as
  // before (Firestore stores it as visitEvaluation.visitEndDate on save). Show
  // the date just picked too, not only the stored one.
  const endDateValue = formData['visitEvaluation.visitEndDate'] || formData.visitEvaluation?.visitEndDate || formData.date;
  const endDateLocked = Boolean(formData.visitEvaluation?.visitEndDate);

  return (
    <div className="tw-space-y-4">
      <SectionCard icon={LuClipboardPen} tone="cyan" title={label('sections.type.title')} hint={label('sections.type.hint')} missingCount={missingIn('type')} complete={missingIn('type') === 0}>
        <div className="tw-space-y-4">
          <Field label={label('visitType')} required badge={tag('visitType')}>
            <div id={id('visitType')}>
              <ChoiceChips
                label={label('visitType')}
                value={formData.visitType}
                onChange={(value) => onChange('visitType', value)}
                disabled={isReadOnly}
                options={VISIT_TYPES.map((type) => ({ value: type, label: label(`visitTypes.${type}`), icon: VISIT_TYPE_ICONS[type] }))}
              />
            </div>
          </Field>

          {formData.visitType === 'hastane' && (
            <CheckRow
              id="vm-screenFail"
              label={(
                <>
                  <span className="tw-block tw-font-semibold">{label('screenFail')}</span>
                  <span className="tw-mt-0.5 tw-block tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('screenFailHint')}</span>
                </>
              )}
              checked={formData.screenFail === true}
              onChange={(checked) => onChange('screenFail', checked)}
              disabled={isReadOnly}
            >
              {formData.screenFail === true && (
                <p className="tw-rounded-xl tw-bg-amber-400/10 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-leading-snug tw-text-amber-800 dark:tw-text-amber-200">
                  {label('screenFailNote')}
                </p>
              )}
            </CheckRow>
          )}

          <div className={FIELD_GRID}>
            <Field label={label('sourceDocument')} required badge={tag('sourceDocument')}>
              <div id={id('sourceDocument')}>
                <ChoiceChips
                  label={label('sourceDocument')}
                  value={formData.sourceDocument}
                  onChange={(value) => onChange('sourceDocument', value)}
                  disabled={isReadOnly}
                  options={[
                    { value: 'yes', label: t('visitModal.common.yes') },
                    { value: 'no', label: t('visitModal.common.no') },
                  ]}
                />
              </div>
            </Field>
            {formData.sourceDocument === 'yes' && (
              <Field label={label('sourceDocumentTime')} htmlFor="vm-sourceDocumentCompletionTime">
                <TextInput
                  id="vm-sourceDocumentCompletionTime"
                  type="time"
                  value={formData.sourceDocumentCompletionTime || ''}
                  onChange={(event) => onChange('sourceDocumentCompletionTime', event.target.value)}
                  disabled={isReadOnly}
                />
              </Field>
            )}
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={LuCalendarClock} tone="sky" title={label('sections.time.title')} hint={label('sections.time.hint')} missingCount={missingIn('time')} complete={missingIn('time') === 0}>
        <div className={FIELD_GRID}>
          <Field label={label('startDate')} htmlFor={id('date')} required badge={tag('date')} hint={formData.date ? label('startDateHint') : undefined}>
            <TextInput
              id={id('date')}
              type="date"
              value={formData.date}
              onChange={(event) => onChange('date', event.target.value)}
              disabled={isReadOnly || !!formData.date}
            />
          </Field>
          <Field label={label('startTime')} htmlFor={id('startTime')} required badge={tag('startTime')}>
            <TextInput
              id={id('startTime')}
              type="time"
              value={formData.startTime}
              onChange={(event) => onChange('startTime', event.target.value)}
              disabled={isReadOnly}
            />
          </Field>
          <Field label={label('endDate')} htmlFor="vm-visitEndDate" hint={label('endDateHint')}>
            <TextInput
              id="vm-visitEndDate"
              type="date"
              value={endDateValue}
              onChange={(event) => onChange('visitEvaluation.visitEndDate', event.target.value)}
              disabled={isReadOnly || endDateLocked}
            />
          </Field>
          <Field label={label('endTime')} htmlFor={id('endTime')} required badge={tag('endTime')}>
            <TextInput
              id={id('endTime')}
              type="time"
              value={formData.endTime}
              onChange={(event) => onChange('endTime', event.target.value)}
              disabled={isReadOnly}
            />
          </Field>
        </div>
      </SectionCard>

      <SectionCard icon={LuUser} tone="teal" title={label('sections.patient.title')} hint={label('sections.patient.hint')} missingCount={missingIn('patient')} complete={missingIn('patient') === 0}>
        <div className={FIELD_GRID}>
          <Field label={label('patientId')} htmlFor={id('patientId')} required badge={tag('patientId')} hint={label('patientHint')} className="sm:tw-col-span-2">
            {loadingPatients ? (
              <TextInput id={id('patientId')} type="text" value={label('patientsLoading')} disabled />
            ) : projectPatients.length > 0 ? (
              <NativeSelect
                id={id('patientId')}
                value={normalizePatientId(formData.patientId)}
                onChange={(event) => onChange('patientId', event.target.value)}
                disabled={isReadOnly}
              >
                <option value="">{label('patientSelect')}</option>
                {projectPatients.map((patient) => (
                  <option key={patient.id} value={patient.patientId}>
                    {patient.patientId} {patient.status ? `(${patient.status})` : ''}
                  </option>
                ))}
              </NativeSelect>
            ) : (
              <TextInput
                id={id('patientId')}
                type="text"
                value={normalizePatientId(formData.patientId)}
                onChange={(event) => onChange('patientId', event.target.value)}
                disabled={isReadOnly}
                placeholder={label('patientManual')}
              />
            )}
          </Field>
          <Field label={label('visitNumber')} htmlFor="vm-visitNumber">
            <TextInput
              id="vm-visitNumber"
              type="number"
              min="1"
              value={formData.visitNumber}
              onChange={(event) => onChange('visitNumber', event.target.value)}
              disabled={isReadOnly}
              placeholder={label('visitNumberPlaceholder')}
            />
          </Field>
          <Field label={label('doctor')} htmlFor={id('doctor')} required badge={tag('doctor')}>
            <TextInput
              id={id('doctor')}
              type="text"
              value={formData.doctor}
              onChange={(event) => onChange('doctor', event.target.value)}
              disabled={isReadOnly}
              placeholder={label('doctorPlaceholder')}
            />
          </Field>
        </div>
      </SectionCard>

      <SectionCard icon={LuMapPin} tone="indigo" title={label('sections.location.title')} hint={label('sections.location.hint')} missingCount={missingIn('location')} complete={missingIn('location') === 0}>
        <div className={FIELD_GRID}>
          <Field
            label={label('hospital')}
            htmlFor={id('hospital')}
            required
            badge={tag('hospital')}
            className="sm:tw-col-span-2"
            hint={loading
              ? label('hospitalsLoading')
              : locationAutoFilledFrom && (locationAutoFilledFrom === 'project' ? label('autoFilledProject') : label('autoFilledPrevious'))}
          >
            <NativeSelect
              id={id('hospital')}
              disabled={loading || isReadOnly}
              // A visit saved against a duplicate document still has to show
              // its merged option as the selected one.
              value={resolveCanonicalCenterId(canonicalIdById, formData.hospital)}
              onChange={(event) => onHospitalChange(event.target.value)}
            >
              <option value="">{t('visitModal.common.pleaseSelect')}</option>
              {myProjectCenters.length > 0 && (
                <optgroup label={label('myCenters')}>
                  {myProjectCenters.map((hospital) => (
                    <option key={hospital.id} value={hospital.id}>{hospital.name}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label={myProjectCenters.length > 0 ? label('otherCenters') : label('centers')}>
                {otherCenters.map((hospital) => (
                  <option key={hospital.id} value={hospital.id}>{hospital.name}</option>
                ))}
              </optgroup>
            </NativeSelect>
          </Field>
          <Field label={label('city')} htmlFor={id('city')} required badge={tag('city')} hint={loading ? label('citiesLoading') : label('cityHint')}>
            <NativeSelect
              id={id('city')}
              disabled={loading || isReadOnly}
              value={formData.city}
              onChange={(event) => onChange('city', event.target.value)}
            >
              <option value="">{t('visitModal.common.pleaseSelect')}</option>
              {cities.map((city, index) => (
                <option key={index} value={city}>{city}</option>
              ))}
            </NativeSelect>
          </Field>
          <Field label={label('travelType')} required badge={tag('travelType')}>
            <div id={id('travelType')}>
              <ChoiceChips
                label={label('travelType')}
                value={formData.travelType}
                onChange={(value) => onChange('travelType', value)}
                disabled={isReadOnly}
                options={[
                  { value: 'local', label: label('travelTypes.local') },
                  { value: 'outside', label: label('travelTypes.outside') },
                ]}
              />
            </div>
          </Field>
        </div>
      </SectionCard>

      <SectionCard icon={LuStickyNote} tone="slate" title={label('sections.notes.title')} hint={label('sections.notes.hint')}>
        <Field label={label('information')} htmlFor="vm-information">
          <TextArea
            id="vm-information"
            rows={3}
            value={formData.information}
            onChange={(event) => onChange('information', event.target.value)}
            disabled={isReadOnly}
            placeholder={label('informationPlaceholder')}
          />
        </Field>
      </SectionCard>
    </div>
  );
};

VisitFormStep.propTypes = {
  formData: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  loading: PropTypes.bool,
  loadingPatients: PropTypes.bool,
  projectPatients: PropTypes.array.isRequired,
  myProjectCenters: PropTypes.array.isRequired,
  otherCenters: PropTypes.array.isRequired,
  canonicalIdById: PropTypes.object.isRequired,
  cities: PropTypes.array.isRequired,
  locationAutoFilledFrom: PropTypes.string,
  missingKeys: PropTypes.instanceOf(Set).isRequired,
  onChange: PropTypes.func.isRequired,
  onHospitalChange: PropTypes.func.isRequired,
};

export default VisitFormStep;
